<template>
  <div v-if="data" class="relative h-full pb-4 overflow-x-auto bg-white">
    <table v-if="data" class="w-full text-sm text-left text-gray-900 divide-y divide-gray-200">
      <tbody>
        <BaseDetailsTableRow
          :name="$t('representative.representative')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            :mode="'view'"
            :model-value="data.representative?.name || '-'"
            color="base"
            flat
            :warning="getFieldSoftError(['representativeId'])"
            show-error-as-tooltip />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('representative.representativeSite')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            :mode="'view'"
            :model-value="data.representativeSite?.name || '-'"
            color="base"
            flat
            :warning="getFieldSoftError(['representativeSiteId'])"
            show-error-as-tooltip />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('representative.documentDescription')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            :mode="mode"
            v-model="data.documentDescription"
            color="base"
            flat
            :warning="getFieldSoftError(['documentDescription'])"
            show-error-as-tooltip />
        </BaseDetailsTableRow>

        <!-- Company Document Type Information -->
        <BaseDetailsTableRow
          :name="$t('representative.documentTypeCode')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            v-if="data.companyDocumentType"
            :mode="mode"
            v-model="data.companyDocumentType.code"
            color="base"
            flat
            :warning="getFieldSoftError(['companyDocumentTypeId', 'representativeDocumentTypeId'])"
            show-error-as-tooltip
            @click="isDocumentTypeModalOpen = true" />
          <BaseInput
            v-else
            :mode="mode"
            color="base"
            flat
            :disabled="!data.representativeDocumentTypeCode"
            :warning="getFieldSoftError(['companyDocumentTypeId', 'representativeDocumentTypeId'])"
            show-error-as-tooltip
            @click="isDocumentTypeModalOpen = true" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('representative.documentTypeRemotedCode')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            :mode="'view'"
            v-model="data.representativeDocumentTypeCode"
            color="base"
            flat
            :warning="getFieldSoftError(['representativeDocumentTypeCode'])"
            show-error-as-tooltip />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('representative.documentSeriesRemotedCode')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            :mode="'view'"
            :model-value="data.representativeDocumentSeriesCode || '-'"
            color="base"
            flat
            :warning="getFieldSoftError(['representativeDocumentSeriesCode'])"
            show-error-as-tooltip />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('representative.documentNumber')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            :mode="mode"
            v-model="data.documentNumber"
            color="base"
            flat
            :warning="getFieldSoftError(['documentNumber'])"
            show-error-as-tooltip />
        </BaseDetailsTableRow>

        <!-- Company Payment Type Information -->
        <BaseDetailsTableRow
          :name="$t('representative.paymentTypeCode')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            v-if="data.companyPaymentType"
            :mode="mode"
            v-model="data.companyPaymentType.code"
            color="base"
            flat
            :warning="getFieldSoftError(['companyPaymentTypeId', 'representativePaymentTypeId'])"
            show-error-as-tooltip
            @click="isPaymentTypeModalOpen = true" />
          <BaseInput
            v-else
            :mode="mode"
            color="base"
            flat
            :disabled="!data.representativePaymentType"
            :warning="getFieldSoftError(['companyPaymentTypeId', 'representativePaymentTypeId'])"
            show-error-as-tooltip
            @click="isPaymentTypeModalOpen = true" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('representative.paymentTypeRemotedCode')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            :mode="'view'"
            v-model="data.representativePaymentTypeCode"
            color="base"
            flat
            :warning="getFieldSoftError(['representativePaymentTypeCode'])"
            show-error-as-tooltip />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('document-type.company')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseSelectDropdown
            :items="dropdownCompanies"
            :selected-item="selectedCompany"
            name="companies"
            flat
            :mode="mode"
            @update:selected="handleSelectCompany"
            clearable
            show-error-as-tooltip
            :error="
              validations?.companyId.required.$invalid
                ? validations?.companyId.required.$message
                : ''
            "
            :warning="getFieldSoftError(['companyId'])" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('representative.companySite')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseSelectDropdown
            :items="filteredDropdownCompanySites"
            :selected-item="selectedCompanySite"
            name="companySites"
            flat
            :mode="mode"
            @update:selected="handleSelectCompanySite"
            clearable
            show-error-as-tooltip
            :error="
              validations?.companySiteId.required.$invalid
                ? validations?.companySiteId.required.$message
                : ''
            "
            :warning="getFieldSoftError(['companySiteId'])" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('representative.officialDate')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <div v-if="mode == 'view'">
            {{ isoDateToFullDate(data.officialDate) || '-' }}
          </div>
          <DateTimePicker
            v-else
            ref="officialDatePickerRef"
            v-model="selectedOfficialDate"
            start-empty
            :show-time-picker="false"
            @update:modelValue="onDateChange">
          </DateTimePicker>
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('representative.documentHeaderRowId')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            :mode="mode"
            v-model="data.documentHeaderRowId"
            color="base"
            flat
            :warning="getFieldSoftError(['documentHeaderRowId'])"
            show-error-as-tooltip />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('representative.source')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            :mode="mode"
            v-model="data.source"
            color="base"
            flat
            :warning="getFieldSoftError(['source'])"
            show-error-as-tooltip />
        </BaseDetailsTableRow>
      </tbody>
    </table>

    <DCDocumentTypeModalCreateEdit
      v-if="isDocumentTypeModalOpen && data.representativeDocumentType"
      :document-type="data.representativeDocumentType"
      :mode="'assign'"
      :view-only="mode === 'view'"
      :representative-id="data.representativeId"
      :existing-document-types="representativeDocumentTypes"
      @close-modal="isDocumentTypeModalOpen = false"
      @save="onDocumentTypeModalSave" />

    <DCPaymentTypeModalCreateEdit
      v-if="isPaymentTypeModalOpen && data.representativePaymentType"
      mode="assign"
      :payment-type="data.representativePaymentType"
      :representative-id="data?.representativeId"
      :representative="data?.representative"
      @closeModal="isPaymentTypeModalOpen = false"
      @save="onPaymentTypeModalSave" />
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import type { DCDocumentHeader, DCDocumentType, DCPaymentType } from '@/types/DataCollection';
import DCDocumentTypeModalCreateEdit from '@/components/traders/DCDocumentTypeModalCreateEdit.vue';
import DCPaymentTypeModalCreateEdit from '@/components/traders/DCPaymentTypeModalCreateEdit.vue';
import BaseDetailsTableRow from '@/components/common/BaseDetailsTableRow.vue';
import BaseSelectDropdown from '../common/BaseSelectDropdown.vue';
import BaseInput from '../common/BaseInput.vue';
import { onMounted, ref, watch, computed } from 'vue';
import { useLookupsStore } from '@/stores/lookups';
import { storeToRefs } from 'pinia';
import type { SelectDropdownItem } from '@/types/Base';
import type { Validation } from '@vuelidate/core';
import DateTimePicker from '../common/DateTimePicker.vue';
import {
  datepickerToServerDateFormat,
  isoDateToDatepickerFormat,
  isoDateToFullDate,
} from '@/utils/date';
import { getDCDocumentTypes } from '@/services/api/dataCollection';

const { t: $t } = useI18n();
const { getCompaniesLookup, getCompanySitesLookup, getDocumentTypesLookup, getPaymentTypesLookup } =
  useLookupsStore();
const {
  dropdownCompanies,
  dropdownCompanySites,
  dropdownDocumentTypes,
  dropdownPaymentTypes,
  companySites,
} = storeToRefs(useLookupsStore());

const data = defineModel<DCDocumentHeader | null>();

const props = defineProps<{
  mode: 'view' | 'edit';
  isHorizontalLayout?: boolean;
  validations?: Validation;
  resetState?: boolean;
}>();

function getFieldSoftError(fieldNames: string[]): string {
  if (!props.validations || props.mode === 'view') return '';

  for (const fieldName of fieldNames) {
    const validationField = props.validations[fieldName];
    if (validationField?.dynamicRequired?.$invalid) {
      return validationField.dynamicRequired.$message;
    }
  }

  return '';
}

const selectedCompany = ref<SelectDropdownItem | null>(null);
const selectedCompanySite = ref<SelectDropdownItem | null>(null);
const selectedDocumentType = ref<SelectDropdownItem | null>(null);
const selectedPaymentType = ref<SelectDropdownItem | null>(null);

const representativeDocumentTypes = ref<DCDocumentType[]>([]);

const filteredDropdownCompanySites = computed(() => {
  if (!selectedCompany.value) {
    return dropdownCompanySites.value;
  }
  const filteredSites = companySites.value.filter(
    (site) => site.companyID === selectedCompany.value?.value
  );
  return filteredSites.map((site) => ({
    value: site.id,
    name: site.name ?? '',
  }));
});

onMounted(async () => {
  await getCompaniesLookup();
  await getCompanySitesLookup();
  await getDocumentTypesLookup();
  await getPaymentTypesLookup();

  representativeDocumentTypes.value =
    (await getDCDocumentTypes(data.value?.representativeId)) ?? [];
  initDropdowns();
});

function initDropdowns() {
  if (!data.value) return;

  if (data.value?.officialDate) {
    const date = isoDateToFullDate(data.value.officialDate);
    selectedOfficialDate.value = isoDateToDatepickerFormat(date);
  }

  if (data.value.companyId) {
    const company = dropdownCompanies.value.find((x) => x.value === data.value?.companyId);
    if (company) selectedCompany.value = company;
  }

  if (data.value.companySiteId) {
    const companySite = dropdownCompanySites.value.find(
      (x) => x.value === data.value?.companySiteId
    );
    if (companySite) selectedCompanySite.value = companySite;
  }

  if (data.value.companyDocumentTypeId) {
    const documentType = dropdownDocumentTypes.value.find(
      (x) => x.value === data.value?.companyDocumentTypeId
    );
    if (documentType) selectedDocumentType.value = documentType;
  }

  if (data.value.companyPaymentTypeId) {
    const paymentType = dropdownPaymentTypes.value.find(
      (x) => x.value === data.value?.companyPaymentTypeId
    );
    if (paymentType) selectedPaymentType.value = paymentType;
  }
}

function handleSelectCompany(item: SelectDropdownItem | null) {
  if (!data.value) return;
  selectedCompany.value = item;
  data.value.companyId = item ? item.value.toString() : '';

  // Clear company site if it doesn't belong to the selected company
  if (selectedCompanySite.value && item) {
    const currentSiteValid = companySites.value.find(
      (site) => site.id === selectedCompanySite.value?.value && site.companyID === item.value
    );
    if (!currentSiteValid) {
      selectedCompanySite.value = null;
      data.value.companySiteId = '';
    }
  } else if (!item) {
    // If no company selected, clear company site
    selectedCompanySite.value = null;
    data.value.companySiteId = '';
  }
}

function handleSelectCompanySite(item: SelectDropdownItem | null) {
  if (!data.value) return;
  selectedCompanySite.value = item;
  data.value.companySiteId = item ? item.value.toString() : '';
}

function onDocumentTypeModalSave(documentType: DCDocumentType) {
  isDocumentTypeModalOpen.value = false;
  if (!data.value) return;
  data.value.companyDocumentTypeId = documentType.documentTypeId;
  data.value.companyDocumentType = documentType.documentType;
  data.value.representativeDocumentType = documentType;
}

function onPaymentTypeModalSave(paymentType: DCPaymentType) {
  isPaymentTypeModalOpen.value = false;
  if (!data.value) return;
  data.value.companyPaymentTypeId = paymentType.paymentTypeId;
  data.value.companyPaymentType = paymentType.paymentType;
  data.value.representativePaymentType = paymentType;
}

const isDocumentTypeModalOpen = ref(false);
const isPaymentTypeModalOpen = ref(false);

const officialDatePickerRef = ref();
const selectedOfficialDate = ref<string>('');
function onDateChange() {
  if (!data.value) return;
  data.value.officialDate = datepickerToServerDateFormat(selectedOfficialDate.value);
}

watch(
  () => props.resetState,
  () => {
    initDropdowns();
  }
);
</script>
